import { buildCollection } from '@firecms/core';
import { CollectionSourceProps } from '~/common';

export interface ClientFeatures {
    dropin: boolean;
    fullApi: boolean;
    historicalRoutes: boolean;
    historicalSessions: boolean;
    hybridApi: boolean;
    sms: boolean;
}

export interface Client {
    name: string;
    otaCode: string;
    providerCode: string;
    apiKey: string;
    pass: string;
    supportEmail: string;
    supportWeb: string;
    enableSessionBeforeDepartureMinutes: number;
    operatorsNotAllowed: string[];
    features: ClientFeatures;
    // Read-only stats
    canCreateCounter: number;
    canCreateFailedCounter: number;
    cancelCounter: number;
    createCounter: number;
    createFailedCounter: number;
    error_db: number;
    error_other: number;
    error_session: number;
    error_system: number;
    error_tracking: number;
    error_utils: number;
    history_session: number;
    history_trip: number;
    mobile_android_count: number;
    mobile_ios_count: number;
}

export const clientsCollection = ({ dataSourceDelegate }: CollectionSourceProps) =>
    buildCollection<Client>({
        name: 'Clients',
        singularName: 'Client',
        id: 'clients',
        path: 'clients',
        group: 'Management',
        icon: 'business',
        textSearchEnabled: true,
        titleProperty: 'name',
        customId: true,
        callbacks: {
            onPreSave: ({ values, entityId }) => ({ ...values, otaCode: entityId ?? values.otaCode }),
        },
        overrides: {
            dataSourceDelegate,
        },
        properties: {
            name: {
                dataType: 'string',
                name: 'Name',
                validation: { required: true },
            },
            otaCode: {
                dataType: 'string',
                name: 'OTA Code',
                description: 'Synced from document ID — do not edit directly',
                readOnly: true,
            },
            providerCode: {
                dataType: 'string',
                name: 'Provider Code',
                description: 'e.g. DIRECT_FERRIES',
            },
            apiKey: {
                dataType: 'string',
                name: 'API Key',
            },
            pass: {
                dataType: 'string',
                name: 'Password',
            },
            supportEmail: {
                dataType: 'string',
                name: 'Support Email',
                email: true,
            },
            supportWeb: {
                dataType: 'string',
                name: 'Support URL',
                url: true,
            },
            enableSessionBeforeDepartureMinutes: {
                dataType: 'number',
                name: 'Session Enable Before Departure (min)',
                defaultValue: 240,
            },
            operatorsNotAllowed: {
                dataType: 'array',
                name: 'Operators Not Allowed',
                of: {
                    dataType: 'string',
                },
            },
            features: {
                dataType: 'map',
                name: 'Features',
                properties: {
                    dropin: {
                        dataType: 'boolean',
                        name: 'Drop-in',
                    },
                    fullApi: {
                        dataType: 'boolean',
                        name: 'Full API',
                    },
                    historicalRoutes: {
                        dataType: 'boolean',
                        name: 'Historical Routes',
                    },
                    historicalSessions: {
                        dataType: 'boolean',
                        name: 'Historical Sessions',
                    },
                    hybridApi: {
                        dataType: 'boolean',
                        name: 'Hybrid API',
                    },
                    sms: {
                        dataType: 'boolean',
                        name: 'SMS',
                    },
                },
            },
            // ── Read-only stats ─────────────────────────────────────────────
            canCreateCounter: {
                dataType: 'number',
                name: 'Can Create',
                readOnly: true,
            },
            canCreateFailedCounter: {
                dataType: 'number',
                name: 'Can Create Failed',
                readOnly: true,
            },
            cancelCounter: {
                dataType: 'number',
                name: 'Cancelled',
                readOnly: true,
            },
            createCounter: {
                dataType: 'number',
                name: 'Created',
                readOnly: true,
            },
            createFailedCounter: {
                dataType: 'number',
                name: 'Create Failed',
                readOnly: true,
            },
            error_db: {
                dataType: 'number',
                name: 'Errors: DB',
                readOnly: true,
            },
            error_other: {
                dataType: 'number',
                name: 'Errors: Other',
                readOnly: true,
            },
            error_session: {
                dataType: 'number',
                name: 'Errors: Session',
                readOnly: true,
            },
            error_system: {
                dataType: 'number',
                name: 'Errors: System',
                readOnly: true,
            },
            error_tracking: {
                dataType: 'number',
                name: 'Errors: Tracking',
                readOnly: true,
            },
            error_utils: {
                dataType: 'number',
                name: 'Errors: Utils',
                readOnly: true,
            },
            history_session: {
                dataType: 'number',
                name: 'History: Sessions',
                readOnly: true,
            },
            history_trip: {
                dataType: 'number',
                name: 'History: Trips',
                readOnly: true,
            },
            mobile_android_count: {
                dataType: 'number',
                name: 'Mobile: Android',
                readOnly: true,
            },
            mobile_ios_count: {
                dataType: 'number',
                name: 'Mobile: iOS',
                readOnly: true,
            },
        },
        propertiesOrder: [
            'name',
            'otaCode',
            'providerCode',
            'apiKey',
            'pass',
            'supportEmail',
            'supportWeb',
            'enableSessionBeforeDepartureMinutes',
            'features',
            'operatorsNotAllowed',
            'createCounter',
            'createFailedCounter',
            'canCreateCounter',
            'canCreateFailedCounter',
            'cancelCounter',
            'error_db',
            'error_other',
            'error_session',
            'error_system',
            'error_tracking',
            'error_utils',
            'history_session',
            'history_trip',
            'mobile_android_count',
            'mobile_ios_count',
        ],
    });
